import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { CaseSelector } from "../../utils/moduleSelectors/caseSelector";
import { fetchCases, transferCase } from "../../../api/case";

const OPEN_STATUSES = ["NEW", "PROCESSING", "FINISHED"];

/**
 * Dialog for å overføre sak-info til en annen sak (samme firma).
 * Viser først åpne saker på samme kunde, deretter firmasikker CaseSelector.
 */
export default function CaseTransferDialog({
  open,
  onClose,
  sourceCase,
  onTransferred,
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState("pick"); // pick | confirm
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [customerCases, setCustomerCases] = useState([]);
  const [loadingCustomerCases, setLoadingCustomerCases] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const sourceId = sourceCase?.id;
  const customerId = sourceCase?.customer?.id || sourceCase?.customer_id;

  const reset = useCallback(() => {
    setStep("pick");
    setTarget(null);
    setNote("");
    setCustomerCases([]);
    setSelectorOpen(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (!customerId || !sourceId) {
      setCustomerCases([]);
      return;
    }

    let cancelled = false;
    setLoadingCustomerCases(true);
    fetchCases({
      page: 1,
      per_page: 25,
      sortBy: "updated_at",
      sort: "desc",
      fields: "id,case_number,title,displayTitle,status,priority,due_date",
      filters: {
        0: { name: "customer", value: String(customerId) },
        1: { name: "status", value: JSON.stringify(OPEN_STATUSES) },
      },
    })
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        setCustomerCases(rows.filter((row) => String(row.id) !== String(sourceId)));
      })
      .catch(() => {
        if (!cancelled) setCustomerCases([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustomerCases(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, customerId, sourceId, reset]);

  const differentCustomer =
    target &&
    customerId &&
    target.customer_id &&
    String(target.customer_id) !== String(customerId);

  const confirmSummary = useMemo(() => {
    if (!target) return "";
    const sourceLabel = `SAK-${sourceCase?.case_number}${sourceCase?.title ? ` – ${sourceCase.title}` : ""}`;
    const targetLabel = `SAK-${target.case_number || target?.displayTitle || target.id}${
      target.title ? ` – ${target.title}` : ""
    }`;
    return t("case.transfer.confirm_summary", {
      source: sourceLabel,
      target: targetLabel,
    });
  }, [target, sourceCase, t]);

  const pickTarget = (row) => {
    if (!row?.id || String(row.id) === String(sourceId)) return;
    setTarget({
      id: row.id,
      case_number: row.case_number,
      title: row.title,
      displayTitle: row.displayTitle || row.primary,
      status: row.status,
      customer_id: row.customer_id || row.customer?.id,
    });
    setStep("confirm");
  };

  const handleSelectorChange = (e) => {
    const row = e?.target?.data;
    if (!row?.id) return;
    pickTarget({
      id: row.id,
      case_number: row.case_number,
      title: row.title,
      displayTitle: row.displayTitle || row.primary,
      status: row.status,
      customer_id: row.customer_id,
      customer: row.customer,
    });
    setSelectorOpen(false);
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.(false);
    reset();
  };

  const handleSubmit = async () => {
    if (!sourceId || !target?.id || submitting) return;
    setSubmitting(true);
    try {
      const res = await transferCase({
        id: sourceId,
        target_case_id: target.id,
        note: note.trim() || undefined,
      });
      const errors = res?.errors || res?.data?.errors;
      if (errors || res?.__error) {
        const msg = Array.isArray(errors)
          ? errors.join(", ")
          : String(errors || t("case.transfer.error"));
        toast.error(msg);
        setSubmitting(false);
        return;
      }
      toast.success(t("case.transfer.success"));
      onTransferred?.(res?.data || res);
      onClose?.(false);
      reset();
    } catch (err) {
      toast.error(err?.message || t("case.transfer.error"));
      setSubmitting(false);
    }
  };

  return (
    <>
      <PMDialog
        open={open && step === "pick"}
        onClose={handleClose}
        title={t("case.transfer.title")}
        size="lg"
        zIndex={3600}
      >
        <div className="space-y-4 text-left">
          <p className="text-sm text-slate-600">{t("case.transfer.intro")}</p>

          {customerId ? (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">
                {t("case.transfer.same_customer_heading")}
              </h3>
              {loadingCustomerCases ? (
                <p className="text-sm text-slate-500">{t("Loading")}</p>
              ) : customerCases.length === 0 ? (
                <p className="text-sm text-slate-500">{t("case.transfer.no_same_customer")}</p>
              ) : (
                <ul className="max-h-56 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-2">
                  {customerCases.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-white"
                        onClick={() => pickTarget(row)}
                      >
                        <ArrowsRightLeftIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                        <span>
                          <span className="font-semibold text-slate-900">
                            SAK-{row.case_number}
                          </span>
                          {row.title ? (
                            <span className="text-slate-600"> – {row.title}</span>
                          ) : null}
                          <span className="mt-0.5 block text-xs text-slate-500">
                            {row.status}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-sm text-amber-700">{t("case.transfer.no_customer_hint")}</p>
          )}

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-800">
              {t("case.transfer.search_heading")}
            </h3>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setSelectorOpen(true)}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
              {t("case.transfer.search_button")}
            </button>
            <CaseSelector
              open={selectorOpen}
              onClose={() => setSelectorOpen(false)}
              onChange={handleSelectorChange}
              label={t("case.transfer.search_button")}
              showField={false}
            />
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={open && step === "confirm" && Boolean(target)}
        onClose={() => {
          if (submitting) return;
          setStep("pick");
        }}
        title={t("case.transfer.confirm_title")}
        size="md"
        zIndex={3700}
      >
        <div className="space-y-3 text-left text-sm text-slate-700">
          <p>{confirmSummary}</p>
          {differentCustomer ? (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-amber-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              {t("case.transfer.different_customer_warning")}
            </p>
          ) : null}
          <p className="text-slate-600">{t("case.transfer.confirm_effects")}</p>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("case.transfer.note_label")}
            </span>
            <textarea
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("case.transfer.note_placeholder")}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              disabled={submitting}
              onClick={() => setStep("pick")}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:opacity-60"
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? t("Loading") : t("case.transfer.confirm_button")}
            </button>
          </div>
        </div>
      </PMDialog>
    </>
  );
}
